"use client";

import React, { useEffect, useCallback } from "react";
import { useCheckoutBundle } from "../hooks/use-checkout-bundle";
import { CheckoutPanel } from "../checkout-panel";
import { CheckoutFooter } from "../checkout-footer";
import { CheckoutSummaryCard } from "../checkout-summary-card";
import { formatPrice } from "../utils";
import { BasketChoiceStep, type BasketChoiceNotice } from "../basket-choice-step";
import { ParticipantsStep, participantsToPayload } from "../participants-step";
import type { DiscountResult } from "../utils/types";

export type CheckoutBundleProps = {
  // Data
  bundle: any;
  course: any;
  user?: any;

  // Callbacks
  onConfirmFreeBooking?: (data: any) => Promise<void>;
  onValidateDiscount?: (code: string, email: string) => Promise<DiscountResult>;
  onSuccess?: (purchase: any) => void;
  onError?: (error: Error | string) => void;
  onClose?: () => void;

  // Config
  currency?: string;
  timezone?: string;
  cancellationPolicyUrl?: string;
  /** Terms settings for dialog display */
  termsSettings?: import("../utils/types").TermsSettings;
  /** Custom terms for dialog display */
  customTerms?: import("../utils/types").CustomTerms;
  /** Callback for webcomponents to open terms dialog in external portal */
  onOpenTermsDialog?: (data: {
    termsSettings?: import("../utils/types").TermsSettings;
    customTerms?: import("../utils/types").CustomTerms;
    timezone?: string;
  }) => void;
  isSendingMaterial?: boolean;
  school?: {
    _id?: string;
    businessCountry?: string;
  };
  /** Callback fired when step changes (for scroll-to-top behavior) */
  onStepChange?: (step: string) => void;
  /** When true, footer is not sticky - flows after content (for iframe embeds) */
  isEmbedMode?: boolean;

  // Basket feature
  basketItemCount?: number;
  onAddToBasket?: (itemData: any) => void;
  onProceedToBasketCheckout: (itemData: any) => void;
  /** When true, skip the basket choice UI and auto-fire onProceedToCheckout on mount (single-item direct lane). */
  directCheckout?: boolean;
  /** Why the host refused the last basket choice; shown on the basket choice step. */
  basketConflict?: BasketChoiceNotice | null;

  // Participants (attendees) — from orchestrator commonProps
  participantsEnabled?: boolean;
  participantLabel?: string;
  participantFields?: import("../participants-step").CheckoutParticipantField[];
  existingParticipants?: import("../participants-step").ExistingParticipant[];
  buyerIsAttendee?: boolean;
  /** Name-later listing: the attendee step accepts empty places. */
  allowUnnamedAttendees?: boolean;
  /** The signed-in customer's identity, for naming the buyer on their own card. */
  customer?: { firstName?: string; lastName?: string; email?: string; phone?: string };
  /** Answers the buyer has already given about themselves elsewhere in this basket. */
  selfSeed?: Record<string, unknown>;
  onSelfAnswersChange?: (answers: Record<string, unknown>) => void;
};

/**
 * Standalone checkout component for purchasing bundles (bundlev2)
 * Flow: (Attendees) -> Basket choice
 * No date or ticket selection; buyer details are collected at basket checkout
 */
export function CheckoutBundle({
  bundle,
  course,
  user,
  onValidateDiscount,
  onClose,
  currency = "GBP",
  timezone,
  cancellationPolicyUrl = "/terms",
  termsSettings,
  customTerms,
  onOpenTermsDialog,
  isSendingMaterial = false,
  onStepChange,
  isEmbedMode = false,
  basketItemCount = 0,
  onAddToBasket,
  onProceedToBasketCheckout,
  directCheckout = false,
  basketConflict = null,
  participantsEnabled = false,
  participantLabel = "Attendees",
  participantFields = [],
  existingParticipants = [],
  buyerIsAttendee = false,
  allowUnnamedAttendees = false,
  customer,
  selfSeed,
  onSelfAnswersChange,
}: CheckoutBundleProps) {
  const checkout = useCheckoutBundle({
    bundle,
    course,
    user,
    isSendingMaterial,
    onValidateDiscount,
    participantsEnabled,
    hasParticipantFields: participantFields.length > 0,
  });

  // Call step change callback for scroll-to-top behavior
  useEffect(() => {
    onStepChange?.(checkout.step);
  }, [checkout.step, onStepChange]);

  // Step labels for breadcrumbs
  const stepLabels: Record<string, string> = {
    participants: participantLabel,
  };

  // Handle adding the bundle to basket as a purchasable item
  const handleAddToBasket = useCallback(() => {
    if (!onAddToBasket) return;

    onAddToBasket({
      type: "bundle",
      bundleTemplateId: bundle?._id || bundle?.id,
      bundleName: bundle?.name,
      bundleDescription: bundle?.description,
      bundleTotalSessions: bundle?.totalSessions,
      bundleExpiresIn: bundle?.expiresIn ?? bundle?.expireInMonths,
      bundleExpirationPeriodType: bundle?.expirationPeriodType ?? "months",
      courseId: course._id || course.id,
      courseName: bundle?.name || "Bundle",
      price: checkout.total,
      currency,
      imageUrl: course.images?.[0]?.url,
      participants: checkout.showParticipantsStep
        ? participantsToPayload(checkout.participantsValue)
        : undefined,
    });
  }, [
    onAddToBasket,
    course,
    bundle,
    checkout.total,
    currency,
    checkout.showParticipantsStep,
    checkout.participantsValue,
  ]);

  const handleProceedToBasketCheckout = useCallback(() => {
    onProceedToBasketCheckout({
      type: "bundle",
      bundleTemplateId: bundle?._id || bundle?.id,
      bundleName: bundle?.name,
      bundleDescription: bundle?.description,
      bundleTotalSessions: bundle?.totalSessions,
      bundleExpiresIn: bundle?.expiresIn ?? bundle?.expireInMonths,
      bundleExpirationPeriodType: bundle?.expirationPeriodType ?? "months",
      courseId: course._id || course.id,
      courseName: bundle?.name || "Bundle",
      price: checkout.total,
      currency,
      imageUrl: course.images?.[0]?.url,
      participants: checkout.showParticipantsStep
        ? participantsToPayload(checkout.participantsValue)
        : undefined,
    });
  }, [
    onProceedToBasketCheckout,
    course,
    bundle,
    checkout.total,
    currency,
    checkout.showParticipantsStep,
    checkout.participantsValue,
  ]);

  // Bundle ticket item for summary
  const ticketItems = [
    {
      name: bundle?.name || "Bundle",
      price: bundle?.price || 0,
      count: 1,
    },
  ];

  return (
    <>
      <CheckoutPanel>
        <CheckoutPanel.Header
          title={bundle?.name || "Bundle"}
          subtitle="Bundle Purchase"
          onClose={onClose}
        />

        <CheckoutPanel.Content isEmbedMode={isEmbedMode}>
          <CheckoutPanel.Main>
            {/* Step Navigation */}
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
              {checkout.visibleSteps.map((stepName, index) => (
                <React.Fragment key={stepName}>
                  {index > 0 && (
                    <span className="text-muted-foreground/70">/</span>
                  )}
                  <button
                    onClick={() =>
                      index < checkout.currentStepIndex &&
                      checkout.goToStep(stepName)
                    }
                    disabled={index > checkout.currentStepIndex}
                    className={`${
                      index === checkout.currentStepIndex
                        ? "text-primary font-medium"
                        : index < checkout.currentStepIndex
                          ? "text-muted-foreground hover:text-primary cursor-pointer"
                          : "text-muted-foreground/70"
                    }`}
                  >
                    {stepLabels[stepName] || stepName}
                  </button>
                </React.Fragment>
              ))}
            </div>

            {/* Participants Step */}
            {checkout.step === "participants" && (
              <ParticipantsStep
                participantLabel={participantLabel}
                fields={participantFields}
                existing={existingParticipants}
                maxParticipants={1}
                value={checkout.participantsValue}
                onChange={checkout.setParticipantsValue}
                onValidationChange={checkout.setIsParticipantsValid}
                buyerIsAttendee={buyerIsAttendee}
                allowUnnamed={allowUnnamedAttendees}
                buyerSeededRef={checkout.buyerAttendeeSeededRef}
                selfSeed={selfSeed}
                onSelfAnswersChange={onSelfAnswersChange}
                buyer={{
                  // The typed personal-details step wins; the signed-in
                  // customer's own record fills in when this flow skips it
                  // (basket-enabled listings collect the buyer downstream).
                  firstName: checkout.personalDetails.guests[0]?.firstName || customer?.firstName,
                  lastName: checkout.personalDetails.guests[0]?.lastName || customer?.lastName,
                  email: checkout.personalDetails.guests[0]?.email || customer?.email,
                  phone: checkout.personalDetails.guests[0]?.phoneNumber || customer?.phone,
                }}
              />
            )}

            {/* Basket Choice Step */}
            {checkout.step === "basketChoice" && (
              <BasketChoiceStep
                itemName={bundle?.name || "Bundle"}
                dateLabel={`${bundle?.ticketCount || 1} class bundle`}
                total={checkout.total}
                currency={currency}
                basketItemCount={basketItemCount}
                onAddToBasket={handleAddToBasket}
                onProceedToCheckout={handleProceedToBasketCheckout}
                directCheckout={directCheckout}
                basketConflict={basketConflict}
              />
            )}
          </CheckoutPanel.Main>

          <CheckoutPanel.Sidebar>
            <CheckoutSummaryCard
              title={bundle?.name || "Bundle"}
              thumbnail={course.images?.[0]?.url}
              description={`${bundle?.ticketCount || 1} class bundle`}
              items={ticketItems}
              total={checkout.total}
              currency={currency}
              discount={checkout.discountAmount}
              discountPercentage={checkout.discount?.type === "PERCENTAGE" ? checkout.discount?.value : undefined}
              onApplyDiscount={checkout.applyDiscount}
              cancellationPolicyUrl={cancellationPolicyUrl}
              termsSettings={termsSettings}
              customTerms={customTerms}
              timezone={timezone}
              onOpenTermsDialog={onOpenTermsDialog}
              // Note: Don't pass bundleInfo when PURCHASING a bundle
              // bundleInfo is for when USING a bundle to book a class
            />

            {/* Bundle description */}
            {bundle?.description && (
              <div className="mt-4 p-4 bg-muted rounded-lg">
                <h4 className="font-semibold text-foreground mb-2">
                  Bundle includes
                </h4>
                <p className="text-sm text-muted-foreground">
                  {bundle.description}
                </p>
              </div>
            )}
          </CheckoutPanel.Sidebar>
        </CheckoutPanel.Content>
      </CheckoutPanel>

      {/* Footer */}
      <CheckoutFooter
        label="Bundle"
        value={bundle?.name}
        total={checkout.total}
        currency={currency}
        showBack={!checkout.isFirstStep}
        onBack={checkout.goToPreviousStep}
        showCancel={!!onClose}
        onCancel={onClose}
        showContinue={checkout.step !== "basketChoice"}
        onContinue={checkout.goToNextStep}
        continueText="Continue"
        continueDisabled={!checkout.canContinue()}
        isEmbedMode={isEmbedMode}
      />
    </>
  );
}
